import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { GhlService } from '../../../services/ghl/ghl.service';

const fontStack = '"Inter", system-ui, -apple-system, sans-serif';

function BarIcon({ bars, muted }: { bars: number; muted?: boolean }) {
  const totalBars = 3;
  const activeBars = muted ? 0 : bars;
  const barHeights = [10, 14, 18];

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '3px', height: 20 }}>
      {[...Array(totalBars)].map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 4,
            height: barHeights[i],
            borderRadius: '2px',
            bgcolor: i < activeBars ? '#111827' : '#D1D5DB',
          }}
        />
      ))}
    </Box>
  );
}

interface Option {
  id: string;
  label: string;
  bars: number;
  mutedBars?: boolean;
}

const options: Option[] = [
  { id: 'first_time', label: 'This is my first time', bars: 1 },
  { id: 'other_platform', label: "I've used another platform", bars: 2 },
  { id: 'regularly', label: 'I run webinars regularly', bars: 3 },
  { id: 'other', label: 'Other', bars: 0, mutedBars: true },
];

interface ExperienceSelectionStepProps {
  email: string;
  initialSelected: string[];
  onComplete: (experience: string[]) => void;
  onSkip: () => void;
  onBack: () => void;
  ghlContactId: string;
}

export default function ExperienceSelectionStep({ email, initialSelected, onComplete, onSkip, onBack, ghlContactId }: ExperienceSelectionStepProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected || []));

  useEffect(() => {
    setSelected(new Set(initialSelected || []));
  }, [initialSelected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);

      if (id === 'other') {
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.clear();
          next.add(id);
        }
      } else {
        if (next.has('other')) {
          next.delete('other');
        }
        next.has(id) ? next.delete(id) : next.add(id);
      }

      return next;
    });
  };

  return (
    <Box sx={{maxWidth: 540}}>
      <Typography
        component="h1"
        sx={{
          fontFamily: fontStack,
          textAlign: 'center',
          color: '#111827',
          fontSize: { xs: '1.25rem', sm: '1.75rem' },
          fontWeight: 700,
          letterSpacing: '-0.02em',
          mb: 0.75,
        }}
      >
        Have you hosted webinars before?
      </Typography>
      <Typography
        sx={{
          fontFamily: fontStack,
          textAlign: 'center',
          color: '#6B7280',
          fontSize: {xs: '0.82rem', sm: '0.92rem'},
          fontWeight: 400,
          mb: 3,
        }}
      >
        So we know how much guidance to give you
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 3 }}>
        {options.map((opt) => {
          const isSelected = selected.has(opt.id);
          return (
            <Box
              key={opt.id}
              onClick={() => toggle(opt.id)}
              sx={{
                fontFamily: fontStack,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.4,
                borderRadius: '10px',
                border: isSelected ? '1.5px solid #111827' : '1.5px solid #E5E7EB',
                bgcolor: isSelected ? '#F3F3F3' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
                '&:hover': {
                  borderColor: isSelected ? '#111827' : '#9CA3AF',
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  "& svg": {
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                <BarIcon bars={opt.bars} muted={opt.mutedBars} />
              </Box>

              <Typography sx={{ flex: 1, fontSize: {xs: '0.88rem', sm: '0.92rem'}, fontWeight: 500, color: '#111827' }}>
                {opt.label}
              </Typography>

              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: isSelected ? 'none' : '1.5px solid #D1D5DB',
                  bgcolor: isSelected ? '#111827' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}
              >
                {isSelected && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden>
                    <path
                      d="M1 4L3.5 6.5L9 1"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={selected.size === 0}
        onClick={async () => {
          onComplete(Array.from(selected));
          let ghlContactIdToUpdate = "";
          if (!ghlContactId) {
            ghlContactIdToUpdate = await GhlService.getContactByEmail(email).then((data: any) => data?.id);
          } else {
            ghlContactIdToUpdate = ghlContactId;
          }
          GhlService.updateContactFields({ contactId: ghlContactIdToUpdate, fields: [{ key: "experience", value: Array.from(selected).join(',') || '' }] });
        }}
        sx={{
          fontFamily: fontStack,
          textTransform: 'none',
          borderRadius: '10px',
          py: 1,
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#FFFFFF',
          bgcolor: '#4058fb',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#3442bd', boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: '#4058fb',
            color: '#C5C7D0',
            opacity: 0.6,
            cursor: 'not-allowed',
            boxShadow: 'none'
          },
        }}
      >
        Continue
      </Button>

      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="text"
          onClick={onBack}
          sx={{
            fontFamily: fontStack,
            textTransform: 'none',
            color: '#3B82F6',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          Back
        </Button>
        <Button
          variant="text"
          onClick={onSkip}
          sx={{
            fontFamily: fontStack,
            textTransform: 'none',
            color: '#6B7280',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          Skip
        </Button>
      </Box>
    </Box>
  );
}
